import { type FormEvent, type ReactElement, type ReactNode, useCallback, useEffect, useMemo, useRef, useState } from 'react';
import type { MetaCheckView, OtpChannel, OtpDeliverySetupStatus, SmsIntegrationView, TestSendRecord, WhatsappIntegrationView } from '@vello/api-client';
import {
  type OtpSetupIssue,
  type OtpSetupStep,
  type OtpSetupStepId,
  type RoutingReviewRow,
  type SmsFormField,
  type SmsIntegrationForm,
  type WebhookSecretsForm,
  type WhatsappWizardForm,
  type WhatsappWizardIssue,
  channelsToEnable,
  classifyTestSend,
  deriveOtpSetupSteps,
  emptySmsForm,
  emptyTemplateRow,
  emptyWebhookSecretsForm,
  enableNeedsAcknowledgement,
  enableReadiness,
  generateVerifyToken,
  getOtpDeliveryStatus,
  getSmsIntegration,
  getTestSend,
  getWhatsappIntegration,
  isTestPhone,
  nextStep,
  normalizeSmsForm,
  normalizeWebhookSecretsForm,
  normalizeWhatsappWizardForm,
  prerequisiteChecklist,
  previousStep,
  resumeStep,
  routingReviewRows,
  runWhatsappMetaCheck,
  sendTestSms,
  sendTestWhatsapp,
  setOtpChannelEnabled,
  setSmsIntegration,
  setWhatsappIntegration,
  setWhatsappWebhookSecrets,
  shouldPollTestSend,
  smsFormFromView,
  testVerdict,
  webhookCallbackUrl,
  whatsappWizardFormFromView,
} from '../../src/index';
import { useAdmin } from '../session';
import { useRouter } from '../router';
import { useConsole } from '../console';
import { useT } from '../i18n/provider';
import { useVocab } from '../i18n/vocab';
import { usePlaceNames } from '../i18n/scope-name';
import { AUTO_REFRESH_MS, useAutoRefresh, Badge, Button, CheckRow, ConfirmDialog, Field, IconButton, Notice, PageHeader, Panel, SegmentedControl, Select, Table, TextInput, type Column, type ConfirmResult, type ConfirmSpec, SectionTitle, FormGrid, Cluster, Grid, Stack, Text, Card, FieldGrid, ScrollX, Split } from '@vello/web-theme';
import { OTP_ISSUE_KEY, SmsGatewayFields } from '../components/otp-slot-fields';

/**
 * Guided OTP delivery setup (D343) — *Configurações › Integrações › Configurar com assistente*. Seven steps an
 * operator without deploy access follows to make WhatsApp-first + SMS-fallback OTP real: prerequisites (with
 * server/provider evidence), WhatsApp credentials, the Meta webhook (secrets sealed in the vault), the SMS gateway,
 * the read-only routing review, a real test send with honest delivery status, and review + enable.
 *
 * Resumable by construction: every step's state is derived from `GET /v1/config/integrations/otp-delivery/status`
 * (the pure `deriveOtpSetupSteps` view-model); each step saves on its own; nothing is marked done client-side. One
 * primary action per step; secrets are write-only (masked hints only); no native browser dialogs.
 */

type Tone = 'success' | 'warning' | 'danger' | 'neutral';
type Msg = { readonly tone: Tone; readonly text: string } | undefined;

const STATE_TONE: Readonly<Record<OtpSetupStep['state'], Tone>> = { done: 'success', attention: 'warning', todo: 'neutral' };
const PRE_TONE = { ok: 'success', failed: 'danger', unknown: 'warning', missing: 'neutral', manual: 'neutral' } as const satisfies Record<string, Tone>;
const INFO_ISSUES: ReadonlySet<OtpSetupIssue> = new Set<OtpSetupIssue>(['WEBHOOK_ENV_SOURCED', 'WA_TEST_NO_RECEIPT', 'WA_NO_BUSINESS_ACCOUNT_ID']);
const WA_ISSUE_KEY: Readonly<Record<WhatsappWizardIssue['code'], string>> = {
  ...OTP_ISSUE_KEY,
  DUPLICATE_LOCALE: 'wiz.err.duplicateLocale',
  INVALID_TEMPLATE_NAME: 'wiz.err.templateName',
  INVALID_TEMPLATE_LANGUAGE: 'wiz.err.templateLanguage',
  INVALID_LOCALE: 'wiz.err.locale',
};
const POLL_MS = 4000;

function useWhen(): (iso: string | undefined) => string {
  const { t, lang } = useT();
  return useCallback(
    (iso) => {
      if (iso === undefined) return t('wiz.never');
      const d = new Date(iso);
      return Number.isNaN(d.getTime()) ? iso : new Intl.DateTimeFormat(lang, { dateStyle: 'short', timeStyle: 'medium' }).format(d);
    },
    [t, lang],
  );
}

function StepFooter({ onBack, children }: { onBack?: (() => void) | undefined; children?: ReactNode }): ReactElement {
  const { t } = useT();
  return (
    <Cluster gap={3} justify="between" wrap>
{onBack !== undefined && <Button onClick={onBack}>{t('wiz.back')}</Button>}
      <Cluster gap={3} wrap>{children}</Cluster>
    </Cluster>
  );
}

interface StepProps {
  readonly status: OtpDeliverySetupStatus;
  readonly refresh: () => Promise<OtpDeliverySetupStatus | undefined>;
  readonly onBack: (() => void) | undefined;
  readonly onNext: () => void;
}

export function OtpSetupWizardScreen(): ReactElement {
  const { runtime } = useAdmin();
  const { navigate } = useRouter();
  const { setRecordTitle } = useConsole();
  const { t } = useT();
  const [status, setStatus] = useState<OtpDeliverySetupStatus | undefined>(undefined);
  const [loadError, setLoadError] = useState<string | undefined>(undefined);
  const [active, setActive] = useState<OtpSetupStepId | undefined>(undefined);
  const [meta, setMeta] = useState<MetaCheckView | undefined>(undefined);
  const headingRef = useRef<HTMLHeadingElement>(null);
  const userMoved = useRef(false);

  useEffect(() => {
    setRecordTitle(t('wiz.title'));
    return () => setRecordTitle(undefined);
  }, [setRecordTitle, t]);

  const refresh = useCallback(async (): Promise<OtpDeliverySetupStatus | undefined> => {
    const res = await getOtpDeliveryStatus(runtime);
    if (!res.ok) {
      setLoadError(t('wiz.load.failed', { code: res.error.code }));
      return undefined;
    }
    setLoadError(undefined);
    setStatus(res.value);
    return res.value;
  }, [runtime, t]);

  // Resume from the SERVER: the first step it does not show as done.
  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getOtpDeliveryStatus(runtime);
      if (cancelled) return;
      if (!res.ok) {
        setLoadError(t('wiz.load.failed', { code: res.error.code }));
        return;
      }
      setStatus(res.value);
      setActive((a) => a ?? resumeStep(deriveOtpSetupSteps(res.value)));
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  // The delivery status stays current by itself (the steps also re-read it after every action they take).
  useAutoRefresh(refresh, { intervalMs: AUTO_REFRESH_MS.queue, enabled: status !== undefined });

  // Move focus to the step heading after an operator-initiated step change (not on first load).
  useEffect(() => {
    if (userMoved.current) headingRef.current?.focus();
  }, [active]);

  const steps = useMemo(() => (status !== undefined ? deriveOtpSetupSteps(status, meta) : []), [status, meta]);
  const current = steps.find((s) => s.id === active);
  const go = (id: OtpSetupStepId | undefined): void => {
    if (id === undefined) return;
    userMoved.current = true;
    setActive(id);
  };

  const renderStep = (s: OtpSetupStep, st: OtpDeliverySetupStatus): ReactElement => {
    const props: StepProps = { status: st, refresh, onBack: previousStep(s.id) !== undefined ? () => go(previousStep(s.id)) : undefined, onNext: () => go(nextStep(s.id)) };
    switch (s.id) {
      case 'prerequisites':
        return <PrerequisitesStep key={s.id} {...props} meta={meta} setMeta={setMeta} />;
      case 'whatsapp':
        return <WhatsappStep key={s.id} {...props} />;
      case 'webhook':
        return <WebhookStep key={s.id} {...props} />;
      case 'sms':
        return <SmsStep key={s.id} {...props} />;
      case 'routing':
        return <RoutingStep key={s.id} {...props} />;
      case 'test':
        return <TestStep key={s.id} {...props} />;
      case 'enable':
        return <EnableStep key={s.id} {...props} />;
    }
  };

  return (
    <>
      <PageHeader
        title={t('wiz.title')}
        subtitle={t('wiz.subtitle')}
        actions={<Button onClick={() => navigate('integrations')}>{t('wiz.backToIntegrations')}</Button>}
      />
      {loadError !== undefined && (
          <Notice tone="danger">{loadError}</Notice>
      )}
      {status === undefined || current === undefined ? (
        loadError === undefined && (
          <Panel>
            <Stack gap={4}>
              <div role="status">
                <Text tone="secondary">{t('wiz.loading')}</Text>
              </div>
            </Stack>
          </Panel>
        )
      ) : (
        <>
          {status.delivery.stagingBypassActive && (
              <Notice tone="neutral">{t('wiz.bypassActive')}</Notice>
          )}
          {status.delivery.liveProviderMode !== 'vault' && (
              <Notice tone="warning">{t('wiz.providersNotVault')}</Notice>
          )}
          <Split aside="md" gap={6}>
            <nav aria-label={t('wiz.steps.aria')}>
              <Stack as="ol" gap={2}>
                {steps.map((s, i) => (
                  <li key={s.id}>
                    <Button variant={s.id === current.id ? 'secondary' : 'ghost'} block aria-current={s.id === current.id ? 'step' : undefined} onClick={() => go(s.id)} className="vello-ab-navbtn">
                      <span>
                        {t('wiz.stepN', { n: i + 1 })} · {t(`wiz.step.${s.id}`)}
                      </span>
                      <Badge tone={STATE_TONE[s.state]}>{t(`wiz.state.${s.state}`)}</Badge>
                    </Button>
                  </li>
                ))}
              </Stack>
            </nav>
            <Panel>
                <Stack gap={4}>
                  <Cluster gap={3} wrap>
                    <h2 ref={headingRef} tabIndex={-1} className="vello-text" data-role="title">
                      {t(`wiz.step.${current.id}`)}
                    </h2>
                    <Badge tone={STATE_TONE[current.state]}>{t(`wiz.state.${current.state}`)}</Badge>
                  </Cluster>
                  <Text as="p" tone="secondary">{t(`wiz.step.${current.id}.hint`)}</Text>
                  {current.issues.length > 0 && (
                    <Stack as="ul" gap={2}>
                      {current.issues.map((issue) => (
                        <li key={issue}>
                          <Notice tone={INFO_ISSUES.has(issue) ? 'neutral' : 'warning'}>{t(`wiz.issue.${issue}`)}</Notice>
                        </li>
                      ))}
                    </Stack>
                  )}
                  {renderStep(current, status)}
                </Stack>
              </Panel>
          </Split>
        </>
      )}
    </>
  );
}

// ---- 1. prerequisites ----------------------------------------------------------------------------------------

function PrerequisitesStep({ status, onBack, onNext, meta, setMeta }: StepProps & { meta: MetaCheckView | undefined; setMeta: (m: MetaCheckView) => void }): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v, fail, language, has } = useVocab();
  const failureWords = (reason: string | undefined): string => v('otp.failure_reason', reason !== undefined && has('otp.failure_reason', reason) ? reason : 'FAILED');
  /** The evidence line of a checklist item, in words: a failure reason, a code-verification status, a template verdict — a provider's own error code only as "provider code". */
  const detailWords = (detail: string): string => {
    if (has('otp.failure_reason', detail)) return failureWords(detail);
    if (has('whatsapp.code_verification_status', detail)) return v('whatsapp.code_verification_status', detail);
    const template = /^([^:]+):(.+)$/.exec(detail);
    if (template !== null) {
      const [, locale = '', rest = ''] = template;
      if (rest === 'NOT_FOUND') return `${language(locale)}: ${t('wiz.pre.meta.templateMissing')}`;
      const [status = '', category = ''] = rest.split('/');
      return `${language(locale)}: ${v('whatsapp.template_status', status)} · ${v('whatsapp.template_category', category)}`;
    }
    return t('wiz.test.code', { code: detail });
  };
  const when = useWhen();
  const [busy, setBusy] = useState(false);
  const [msg, setMsg] = useState<Msg>(undefined);
  const items = prerequisiteChecklist(status, meta);
  const canCheck = status.whatsapp.configured && status.whatsapp.configComplete;

  const run = async (): Promise<void> => {
    setMsg(undefined);
    setBusy(true);
    const res = await runWhatsappMetaCheck(runtime);
    setBusy(false);
    if (res.ok) setMeta(res.value);
    else setMsg({ tone: 'danger', text: res.error.code === 'CONFLICT' ? t('wiz.pre.meta.notReady') : t('wiz.pre.meta.failed', { code: fail(res.error) }) });
  };

  return (
    <>
      <Stack as="ul" gap={3}>
        {items.map((item) => (
          <Card as="li" key={item.id}>
            <Stack gap={3}>
              <Cluster gap={3} justify="between" wrap>
                <strong>{t(`wiz.pre.${item.id}`)}</strong>
                <Badge tone={PRE_TONE[item.state]}>{t(`wiz.pre.state.${item.state}`)}</Badge>
              </Cluster>
              <Text as="p" tone="secondary">{t(`wiz.pre.${item.id}.hint`)}</Text>
              <Text as="p" role="caption" tone="tertiary">
                {t(`wiz.pre.evidence.${item.evidence}`)}
                {item.detail !== undefined && (
                  <>
                    {' · '}
                    <Text>{detailWords(item.detail)}</Text>
                  </>
                )}
              </Text>
            </Stack>
          </Card>
        ))}
      </Stack>

      <Card as="div">
        <Stack gap={3}>
          <SectionTitle level={3} size="sub">{t('wiz.pre.metaCheck')}</SectionTitle>
          <Text as="p" tone="secondary">{t('wiz.pre.metaCheck.hint')}</Text>
          <Button disabled={busy || !canCheck} onClick={() => void run()}>
            {busy ? t('wiz.pre.metaCheck.busy') : t('wiz.pre.metaCheck')}
          </Button>
          {!canCheck && <Text as="p" role="caption" tone="tertiary">{t('wiz.pre.meta.notReady')}</Text>}
          <div role="status" aria-live="polite">
            {msg !== undefined && (
              <Notice tone={msg.tone}>{msg.text}</Notice>
            )}
            {meta !== undefined && (
              <div>
                <Text as="p" role="caption" tone="tertiary">{t('wiz.pre.meta.checkedAt', { when: when(meta.checkedAt) })}</Text>
                {meta.phoneNumber.ok ? (
                  <FieldGrid>
                    {(
                      [
                        ['wiz.pre.meta.displayNumber', meta.phoneNumber.displayPhoneNumber],
                        ['wiz.pre.meta.verifiedName', meta.phoneNumber.verifiedName],
                        ['wiz.pre.meta.quality', meta.phoneNumber.qualityRating === undefined ? undefined : v('whatsapp.quality_rating', meta.phoneNumber.qualityRating)],
                        ['wiz.pre.meta.codeVerification', meta.phoneNumber.codeVerificationStatus === undefined ? undefined : v('whatsapp.code_verification_status', meta.phoneNumber.codeVerificationStatus)],
                      ] as const
                    ).map(([label, value]) => (
                      <div key={label}>
                        <Text as="dt" role="caption" tone="tertiary">{t(label)}</Text>
                        <Text as="dd" mono>{value ?? '—'}</Text>
                      </div>
                    ))}
                  </FieldGrid>
                ) : (
                  <Notice tone="danger">
                    {t('wiz.pre.meta.phoneFailed', { reason: failureWords(meta.phoneNumber.reason) })}
                    {meta.phoneNumber.errorCode !== undefined && <Text mono> · {t('wiz.test.code', { code: meta.phoneNumber.errorCode })}</Text>}
                  </Notice>
                )}
                {meta.templates.ok ? (
                  <Stack as="ul" gap={2}>
                    {meta.templates.items.map((item) => (
                      <Cluster gap={3} wrap as="li" key={item.locale}>
                        <Text mono>
                          {language(item.locale)} → {item.name} ({language(item.language)})
                        </Text>
                        {item.found ? (
                          <Badge tone={item.status === 'APPROVED' && item.category === 'AUTHENTICATION' ? 'success' : 'danger'}>
                            {v('whatsapp.template_status', item.status)} · {v('whatsapp.template_category', item.category)}
                          </Badge>
                        ) : (
                          <Badge tone="danger">{t('wiz.pre.meta.templateMissing')}</Badge>
                        )}
                      </Cluster>
                    ))}
                    {meta.templates.truncated && <li><Text role="caption" tone="tertiary">{t('wiz.pre.meta.truncated')}</Text></li>}
                  </Stack>
                ) : (
                  <Notice tone={meta.templates.reason === 'NO_BUSINESS_ACCOUNT_ID' ? 'warning' : 'danger'}>
                    {meta.templates.reason === 'NO_BUSINESS_ACCOUNT_ID' ? t('wiz.issue.WA_NO_BUSINESS_ACCOUNT_ID') : t('wiz.pre.meta.templatesFailed', { reason: failureWords(meta.templates.reason) })}
                  </Notice>
                )}
              </div>
            )}
          </div>
        </Stack>
      </Card>

      <StepFooter onBack={onBack}>
        <Button variant="primary" onClick={onNext}>
          {t('wiz.continue')}
        </Button>
      </StepFooter>
    </>
  );
}

// ---- 2. WhatsApp credentials -----------------------------------------------------------------------------------

function WhatsappStep({ status, refresh, onBack, onNext }: StepProps): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const [view, setView] = useState<WhatsappIntegrationView | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState<WhatsappWizardForm>(() => whatsappWizardFormFromView(undefined));
  const [issues, setIssues] = useState<readonly WhatsappWizardIssue[]>([]);
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getWhatsappIntegration(runtime);
      if (cancelled) return;
      if (res.ok) {
        setView(res.value);
        setForm(whatsappWizardFormFromView(res.value));
      } else setMsg({ tone: 'danger', text: t('int.load.failed', { code: res.error.code }) });
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const set = <K extends keyof WhatsappWizardForm>(field: K, value: WhatsappWizardForm[K]): void => setForm((f) => ({ ...f, [field]: value }));
  const setRow = (index: number, patch: Partial<WhatsappWizardForm['rows'][number]>): void => setForm((f) => ({ ...f, rows: f.rows.map((r, j) => (j === index ? { ...r, ...patch } : r)) }));
  const fieldError = (field: WhatsappWizardIssue['field']): string | undefined => {
    const issue = issues.find((i) => i.field === field && i.row === undefined);
    return issue !== undefined ? t(WA_ISSUE_KEY[issue.code]) : undefined;
  };
  const rowErrors = (row: number): string => issues.filter((i) => i.field === 'rows' && i.row === row).map((i) => t(WA_ISSUE_KEY[i.code])).join(' ');
  const locales = [...new Set(form.rows.map((r) => r.locale.trim()).filter((l) => l !== ''))];
  const configured = view?.configured === true || status.whatsapp.configured;

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    const normalized = normalizeWhatsappWizardForm(form, view?.config.enabled === true);
    if (!normalized.ok) {
      setIssues(normalized.error);
      return;
    }
    setIssues([]);
    setBusy(true);
    const res = await setWhatsappIntegration(runtime, normalized.value);
    if (!res.ok) {
      setBusy(false);
      setMsg({ tone: 'danger', text: t('int.save.failed', { code: res.error.code }) });
      return;
    }
    setView(res.value);
    setForm(whatsappWizardFormFromView(res.value));
    await refresh();
    setBusy(false);
    onNext();
  };

  return (
    <form onSubmit={(e) => void onSave(e)} noValidate>
      <Stack gap={4}>
        <FormGrid>
          <Field label={t('int.whatsapp.field.phoneNumberId')} htmlFor="wiz-wa-phone-number-id" hint={fieldError('phoneNumberId') ?? t('wiz.pre.phoneNumber.hint')}>
            <TextInput id="wiz-wa-phone-number-id" inputMode="numeric" value={form.phoneNumberId} onChange={(e) => set('phoneNumberId', e.target.value)} required />
          </Field>
          <Field label={t('int.whatsapp.field.businessAccountId')} htmlFor="wiz-wa-waba-id" hint={fieldError('businessAccountId') ?? t('wiz.pre.waba.hint')}>
            <TextInput id="wiz-wa-waba-id" inputMode="numeric" value={form.businessAccountId} onChange={(e) => set('businessAccountId', e.target.value)} />
          </Field>
          <Field label={t('int.whatsapp.field.apiVersion')} htmlFor="wiz-wa-api-version" hint={fieldError('apiVersion')}>
            <TextInput id="wiz-wa-api-version" value={form.apiVersion} onChange={(e) => set('apiVersion', e.target.value)} placeholder="v21.0" />
          </Field>
          <Field label={t('int.whatsapp.field.accessToken')} htmlFor="wiz-wa-access-token" hint={configured ? t('int.field.apiKey.hintConfigured', { hint: view?.maskedHint ?? status.whatsapp.maskedHint ?? '' }) : t('wiz.pre.accessToken.hint')}>
            <TextInput id="wiz-wa-access-token" type="password" autoComplete="off" value={form.accessToken} onChange={(e) => set('accessToken', e.target.value)} placeholder={configured ? '••••••••' : ''} />
          </Field>
        </FormGrid>

        <Stack as="fieldset" gap={3}>
          <Text as="legend" role="label">{t('wiz.wa.templates')}</Text>
          <Text as="p" tone="secondary">{t('wiz.wa.templates.hint')}</Text>
          <Stack as="ul" gap={3}>
            {form.rows.map((row, i) => (
              <Card as="li" key={i}>
                <Stack gap={3}>
                  <FormGrid>
                    <Field label={t('wiz.wa.row.locale')} htmlFor={`wiz-wa-row-${i}-locale`}>
                      <TextInput id={`wiz-wa-row-${i}-locale`} value={row.locale} onChange={(e) => setRow(i, { locale: e.target.value })} placeholder="pt" />
                    </Field>
                    <Field label={t('wiz.wa.row.name')} htmlFor={`wiz-wa-row-${i}-name`}>
                      <TextInput id={`wiz-wa-row-${i}-name`} value={row.name} onChange={(e) => setRow(i, { name: e.target.value })} placeholder="vello_authentication_pt" />
                    </Field>
                    <Field label={t('wiz.wa.row.language')} htmlFor={`wiz-wa-row-${i}-language`}>
                      <TextInput id={`wiz-wa-row-${i}-language`} value={row.language} onChange={(e) => setRow(i, { language: e.target.value })} placeholder="pt_PT" />
                    </Field>
                  </FormGrid>
                  {rowErrors(i) !== '' && <Notice tone="danger">{t('wiz.wa.row.error', { n: i + 1, message: rowErrors(i) })}</Notice>}
                  {form.rows.length > 1 && (
                    <IconButton icon="x" label={t('wiz.wa.row.remove', { n: i + 1 })} onClick={() => setForm((f) => ({ ...f, rows: f.rows.filter((_, j) => j !== i) }))} />
                  )}
                </Stack>
              </Card>
            ))}
          </Stack>
          {fieldError('rows') !== undefined && (
            <Notice tone="danger">{fieldError('rows')}</Notice>
          )}
          <Button onClick={() => setForm((f) => ({ ...f, rows: [...f.rows, emptyTemplateRow()] }))}>{t('wiz.wa.row.add')}</Button>
        </Stack>

        <FormGrid>
          <Field label={t('int.otp.field.defaultLocale')} htmlFor="wiz-wa-default-locale" hint={fieldError('defaultLocale') ?? t('int.otp.field.defaultLocale.hint')}>
            <Select id="wiz-wa-default-locale" value={form.defaultLocale} onChange={(e) => set('defaultLocale', e.target.value)}>
              <option value="">{t('wiz.wa.defaultLocale.pick')}</option>
              {[...new Set([...locales, ...(form.defaultLocale !== '' ? [form.defaultLocale] : [])])].map((l) => (
                <option key={l} value={l}>
                  {l}
                </option>
              ))}
            </Select>
          </Field>
          <Field label={t('int.otp.field.cost')} htmlFor="wiz-wa-cost-minor" hint={fieldError('approxCost') ?? t('int.otp.field.cost.hint')}>
            <Cluster gap={2}>
              <TextInput id="wiz-wa-cost-minor" inputMode="numeric" value={form.approxCostMinor} onChange={(e) => set('approxCostMinor', e.target.value)} placeholder="4" />
              <TextInput id="wiz-wa-cost-currency" aria-label={t('int.otp.field.cost.currency')} value={form.approxCostCurrency} onChange={(e) => set('approxCostCurrency', e.target.value)} placeholder="USD" className="vello-ab-narrow" />
            </Cluster>
          </Field>
        </FormGrid>

        <Text as="p" role="caption" tone="tertiary">{t('wiz.wa.enabledUnchanged')}</Text>
        {msg !== undefined && (
          <div role="status" aria-live="polite">
            <Notice tone={msg.tone}>{msg.text}</Notice>
          </div>
        )}
        <StepFooter onBack={onBack}>
          <Button type="submit" variant="primary" disabled={busy || !loaded}>
            {busy ? t('int.save.busy') : t('wiz.saveContinue')}
          </Button>
        </StepFooter>
      </Stack>
    </form>
  );
}

// ---- 3. webhook ---------------------------------------------------------------------------------------------

function WebhookStep({ status, refresh, onBack, onNext }: StepProps): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const when = useWhen();
  const [form, setForm] = useState<WebhookSecretsForm>(() => emptyWebhookSecretsForm());
  const [errors, setErrors] = useState<Partial<Record<'appSecret' | 'verifyToken' | 'form', string>>>({});
  const [msg, setMsg] = useState<Msg>(undefined);
  const [copyMsg, setCopyMsg] = useState<string | undefined>(undefined);
  const [busy, setBusy] = useState(false);
  const w = status.webhook;
  const callback = webhookCallbackUrl(runtime.apiBaseUrl, w.callbackPath);
  const dirty = form.appSecret.trim() !== '' || form.verifyToken.trim() !== '';

  const copy = async (): Promise<void> => {
    try {
      await navigator.clipboard.writeText(callback.url);
      setCopyMsg(t('wiz.copied'));
    } catch {
      setCopyMsg(t('wiz.copyFailed'));
    }
  };
  const generate = (): void => {
    const bytes = new Uint8Array(24);
    crypto.getRandomValues(bytes);
    setForm((f) => ({ ...f, verifyToken: generateVerifyToken(bytes) }));
  };

  const onSubmit = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    if (!dirty) {
      onNext();
      return;
    }
    setMsg(undefined);
    const normalized = normalizeWebhookSecretsForm(form);
    if (!normalized.ok) {
      const next: Partial<Record<'appSecret' | 'verifyToken' | 'form', string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(issue.code === 'NOTHING_TO_SAVE' ? 'wiz.err.nothingToSave' : 'wiz.err.webhookSecret');
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const res = await setWhatsappWebhookSecrets(runtime, normalized.value);
    if (res.ok) {
      setForm(emptyWebhookSecretsForm());
      await refresh();
      setMsg({ tone: 'success', text: t('wiz.saved') });
    } else setMsg({ tone: 'danger', text: t('int.save.failed', { code: res.error.code }) });
    setBusy(false);
  };

  const secretRow = (labelId: string, state: OtpDeliverySetupStatus['webhook']['appSecret']): ReactElement => (
    <Cluster gap={3} justify="between" wrap as="li">
      <span>{t(labelId)}</span>
      <Cluster gap={3} wrap>
        {state.maskedHint !== undefined && <Text mono>{t('wiz.hook.masked', { hint: state.maskedHint })}</Text>}
        <Badge tone={state.source === 'vault' ? 'success' : state.source === 'env' ? 'neutral' : 'warning'}>{v('webhook.secret_source', state.source)}</Badge>
      </Cluster>
    </Cluster>
  );

  return (
    <form onSubmit={(e) => void onSubmit(e)} noValidate>
      <Stack gap={4}>
        <Field label={t('wiz.hook.callback')} htmlFor="wiz-hook-callback" hint={t('wiz.hook.instructions')}>
          <Cluster gap={2} wrap>
            <TextInput id="wiz-hook-callback" className="tnum" readOnly value={callback.url} onFocus={(e) => e.currentTarget.select()} />
            <Button onClick={() => void copy()}>{t('wiz.copy')}</Button>
          </Cluster>
        </Field>
        <div role="status" aria-live="polite">
          {copyMsg !== undefined && <Text as="p" role="caption" tone="tertiary">{copyMsg}</Text>}
        </div>
        {!callback.https && (
            <Notice tone="warning">{t('wiz.hook.notHttps')}</Notice>
        )}

        <Grid min="md" gap={4}>
          <Card as="div">
            <Stack as="ul" gap={2}>
              {secretRow('wiz.hook.appSecret', w.appSecret)}
              {secretRow('wiz.hook.verifyToken', w.verifyToken)}
            </Stack>
            <Text as="p" role="caption" tone="tertiary">{t('wiz.hook.envNote')}</Text>
          </Card>
          <Card as="div">
            <Stack gap={3}>
              <SectionTitle level={3} size="sub">{t('wiz.hook.activity')}</SectionTitle>
              {w.activityTracked ? (
                <Stack as="ul" gap={1}>
                  <li><Text role="caption" tone="tertiary">{t('wiz.hook.lastHandshake', { when: when(w.activity.lastHandshakeAt) })}</Text></li>
                  <li><Text role="caption" tone="tertiary">{t('wiz.hook.lastEvent', { when: when(w.activity.lastEventAt) })}</Text></li>
                  <li><Text role="caption" tone="tertiary">{t('wiz.hook.lastRejected', { when: when(w.activity.lastSignatureRejectedAt) })}</Text></li>
                </Stack>
              ) : (
                <Text as="p" role="caption" tone="tertiary">{t('wiz.hook.activityUntracked')}</Text>
              )}
            </Stack>
          </Card>
        </Grid>

        <FormGrid>
          <Field label={t('wiz.hook.field.verifyToken')} htmlFor="wiz-hook-verify-token" hint={errors.verifyToken ?? t('wiz.hook.field.verifyToken.hint')}>
            <Cluster gap={2} wrap>
              <TextInput id="wiz-hook-verify-token" autoComplete="off" spellCheck={false} value={form.verifyToken} onChange={(e) => setForm((f) => ({ ...f, verifyToken: e.target.value }))} className="tnum" />
              <Button onClick={generate}>{t('wiz.hook.generate')}</Button>
            </Cluster>
          </Field>
          <Field label={t('wiz.hook.field.appSecret')} htmlFor="wiz-hook-app-secret" hint={errors.appSecret ?? t('wiz.hook.field.appSecret.hint')}>
            <TextInput id="wiz-hook-app-secret" type="password" autoComplete="off" value={form.appSecret} onChange={(e) => setForm((f) => ({ ...f, appSecret: e.target.value }))} />
          </Field>
        </FormGrid>
        <div role="status" aria-live="polite">
          {errors.form !== undefined && <Notice tone="danger">{errors.form}</Notice>}
          {msg !== undefined && <Notice tone={msg.tone}>{msg.text}</Notice>}
        </div>
        <StepFooter onBack={onBack}>
          <Button type="submit" variant="primary" disabled={busy}>
            {busy ? t('int.save.busy') : dirty ? t('wiz.hook.save') : t('wiz.continue')}
          </Button>
        </StepFooter>
      </Stack>
    </form>
  );
}

// ---- 4. SMS gateway -------------------------------------------------------------------------------------------

function SmsStep({ status, refresh, onBack, onNext }: StepProps): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const [view, setView] = useState<SmsIntegrationView | undefined>(undefined);
  const [loaded, setLoaded] = useState(false);
  const [form, setForm] = useState<SmsIntegrationForm>(() => emptySmsForm());
  const [errors, setErrors] = useState<Partial<Record<SmsFormField, string>>>({});
  const [msg, setMsg] = useState<Msg>(undefined);
  const [busy, setBusy] = useState(false);

  useEffect(() => {
    let cancelled = false;
    void (async () => {
      const res = await getSmsIntegration(runtime);
      if (cancelled) return;
      if (res.ok) {
        setView(res.value);
        setForm(smsFormFromView(res.value));
      } else setMsg({ tone: 'danger', text: t('int.load.failed', { code: res.error.code }) });
      setLoaded(true);
    })();
    return () => {
      cancelled = true;
    };
  }, [runtime, t]);

  const onSave = async (e: FormEvent<HTMLFormElement>): Promise<void> => {
    e.preventDefault();
    setMsg(undefined);
    // `enabled` is carried over from the stored slot: saving the gateway never switches real SMS on or off.
    const normalized = normalizeSmsForm({ ...form, enabled: view?.config.enabled === true });
    if (!normalized.ok) {
      const next: Partial<Record<SmsFormField, string>> = {};
      for (const issue of normalized.error) next[issue.field] = t(OTP_ISSUE_KEY[issue.code]);
      setErrors(next);
      return;
    }
    setErrors({});
    setBusy(true);
    const res = await setSmsIntegration(runtime, normalized.value);
    if (!res.ok) {
      setBusy(false);
      setMsg({ tone: 'danger', text: t('int.save.failed', { code: res.error.code }) });
      return;
    }
    setView(res.value);
    setForm(smsFormFromView(res.value));
    await refresh();
    setBusy(false);
    onNext();
  };

  return (
    <form onSubmit={(e) => void onSave(e)} noValidate>
      <Stack gap={4}>
        <SmsGatewayFields idPrefix="wiz-sms" form={form} setForm={setForm} errors={errors} configured={view?.configured === true || status.sms.configured} maskedHint={view?.maskedHint ?? status.sms.maskedHint} />
        <Text as="p" role="caption" tone="tertiary">{t('wiz.sms.enabledUnchanged')}</Text>
        {msg !== undefined && (
          <div role="status" aria-live="polite">
            <Notice tone={msg.tone}>{msg.text}</Notice>
          </div>
        )}
        <StepFooter onBack={onBack}>
          <Button onClick={onNext}>{t('wiz.skip')}</Button>
          <Button type="submit" variant="primary" disabled={busy || !loaded}>
            {busy ? t('int.save.busy') : t('wiz.saveContinue')}
          </Button>
        </StepFooter>
      </Stack>
    </form>
  );
}

// ---- 5. routing review ------------------------------------------------------------------------------------------

function RoutingStep({ status, onBack, onNext }: StepProps): ReactElement {
  const { t } = useT();
  const { v } = useVocab();
  const place = usePlaceNames();
  const rows = routingReviewRows(status);
  const channelCell = (cell: RoutingReviewRow['primary']): ReactElement => (
    <Cluster gap={3} wrap>
      <span>{v('verification.channel', cell.channel)}</span>
      <Text mono>{cell.providerId}</Text>
      {!cell.known && <Badge tone="danger">{t('wiz.routing.unknownProvider')}</Badge>}
    </Cluster>
  );
  const columns: readonly Column<RoutingReviewRow>[] = [
    { key: 'scope', header: t('wiz.routing.col.scope'), render: (r) => (r.scope === 'default' ? t('wiz.routing.default') : <Text mono>{t('wiz.routing.country', { country: place.country(r.countryCode), code: r.callingCode ?? '' })}</Text>) },
    { key: 'primary', header: t('wiz.routing.col.primary'), render: (r) => channelCell(r.primary) },
    {
      key: 'fallback',
      header: t('wiz.routing.col.fallback'),
      render: (r) =>
        r.fallback === undefined ? (
          <Badge tone="warning">{t('wiz.routing.noFallback')}</Badge>
        ) : (
          <Cluster gap={3} wrap>
            {channelCell(r.fallback)}
            {!r.fallbackEffective && <Badge tone="warning">{t('wiz.routing.fallbackOff')}</Badge>}
          </Cluster>
        ),
    },
  ];
  const p = status.policy;
  const policy: readonly (readonly [string, string])[] = [
    ['wiz.routing.policy.fallback', p.smsFallbackEnabled ? t('wiz.yes') : t('wiz.no')],
    ['wiz.routing.policy.auto', p.autoFallbackOnFailure ? t('wiz.yes') : t('wiz.no')],
    ['wiz.routing.policy.delay', t('wiz.routing.seconds', { n: p.smsFallbackDelaySeconds })],
    ['wiz.routing.policy.timeout', t('wiz.routing.seconds', { n: p.primaryDeliveryTimeoutSeconds })],
    ['wiz.routing.policy.cooldown', t('wiz.routing.seconds', { n: p.resendCooldownSeconds })],
    ['wiz.routing.policy.ttl', t('wiz.routing.seconds', { n: p.ttlSeconds })],
  ];
  return (
    <>
      <Cluster gap={3} wrap>
        <Badge tone="neutral">{v('otp.routing_source', status.routing.source)}</Badge>
        <Text role="caption" tone="tertiary">{t('wiz.routing.readOnly', { envVar: status.routing.envVar })}</Text>
      </Cluster>
      <ScrollX>
        <Table columns={columns} rows={rows} rowKey={(r) => r.key} />
      </ScrollX>
      <Card as="div">
        <Stack gap={3}>
          <SectionTitle level={3} size="sub">{t('wiz.routing.policy')}</SectionTitle>
          <FieldGrid>
            {policy.map(([label, value]) => (
              <div key={label}>
                <Text as="dt" role="caption" tone="tertiary">{t(label)}</Text>
                <Text as="dd" mono>{value}</Text>
              </div>
            ))}
          </FieldGrid>
        </Stack>
      </Card>
      <StepFooter onBack={onBack}>
        <Button variant="primary" onClick={onNext}>
          {t('wiz.continue')}
        </Button>
      </StepFooter>
    </>
  );
}

// ---- 6. test send ---------------------------------------------------------------------------------------------

function TestStep({ status, refresh, onBack, onNext }: StepProps): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v, fail, has } = useVocab();
  const failureWords = (reason: string | undefined): string => v('otp.failure_reason', reason !== undefined && has('otp.failure_reason', reason) ? reason : 'FAILED');
  const when = useWhen();
  const [channel, setChannel] = useState<OtpChannel>('whatsapp');
  const [to, setTo] = useState('');
  const [locale, setLocale] = useState('');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | undefined>(undefined);
  const [record, setRecord] = useState<TestSendRecord | undefined>(undefined);
  const [pollStopped, setPollStopped] = useState(false);
  const startedAt = useRef(0);
  const slot = status[channel];

  // Poll one tracked test-send while a receipt can still arrive (bounded); stop at a terminal status.
  useEffect(() => {
    if (record === undefined || record.providerMessageId === undefined || !record.receiptsExpected || classifyTestSend(record).terminal || pollStopped) return;
    const id = record.providerMessageId;
    const ch = record.channel;
    const timer = window.setTimeout(() => {
      if (!shouldPollTestSend(record, startedAt.current, Date.now())) {
        setPollStopped(true);
        return;
      }
      void (async () => {
        const res = await getTestSend(runtime, ch, id);
        if (!res.ok) {
          setPollStopped(true);
          return;
        }
        setRecord(res.value);
        if (classifyTestSend(res.value).terminal) void refresh();
      })();
    }, POLL_MS);
    return () => window.clearTimeout(timer);
  }, [record, pollStopped, runtime, refresh]);

  const send = async (): Promise<void> => {
    setError(undefined);
    if (!isTestPhone(to)) {
      setError(t('int.err.phone'));
      return;
    }
    const previous = slot.lastTest?.sentAt;
    setBusy(true);
    setRecord(undefined);
    setPollStopped(false);
    const res = channel === 'whatsapp' ? await sendTestWhatsapp(runtime, to, locale) : await sendTestSms(runtime, to, locale);
    startedAt.current = Date.now();
    const fresh = await refresh();
    if (res.ok && res.value.providerMessageId !== undefined) {
      const tracked = await getTestSend(runtime, channel, res.value.providerMessageId);
      setRecord(tracked.ok ? tracked.value : fresh?.[channel].lastTest);
      setBusy(false);
      return;
    }
    setBusy(false);
    // The server records every outcome (typed failure reason included); show it only if it is THIS send's record.
    const last = fresh?.[channel].lastTest;
    if (last !== undefined && last.sentAt !== previous) {
      setRecord(last);
      return;
    }
    if (!res.ok) setError(res.error.code === 'CONFLICT' ? t('int.otp.test.notConfigured') : t('int.test.failed', { code: fail(res.error) }));
  };

  const outcome = record !== undefined ? classifyTestSend(record) : undefined;
  const polling = record !== undefined && outcome !== undefined && record.receiptsExpected && !outcome.terminal && !pollStopped;
  const canSend = slot.configured && slot.configComplete;

  return (
    <>
        <SegmentedControl<OtpChannel>
          ariaLabel={t('wiz.test.channel')}
          value={channel}
          onChange={(c) => {
            setChannel(c);
            setLocale('');
            setRecord(undefined);
            setError(undefined);
          }}
          options={[
            { id: 'whatsapp', label: v('verification.channel', 'whatsapp') },
            { id: 'sms', label: v('verification.channel', 'sms') },
          ]}
        />
        <Notice tone="neutral">{t('wiz.test.realMessage')}</Notice>
      {channel === 'whatsapp' && (status.webhook.appSecret.source === 'none' || status.webhook.verifyToken.source === 'none') && (
          <Notice tone="warning">{t('wiz.test.noWebhook')}</Notice>
      )}
      {!canSend && (
          <Notice tone="warning">{t('int.otp.test.notConfigured')}</Notice>
      )}
      <FormGrid>
        <Field label={t('int.otp.field.to')} htmlFor="wiz-test-to" hint={error}>
          <TextInput id="wiz-test-to" type="tel" autoComplete="off" value={to} onChange={(e) => setTo(e.target.value)} placeholder="+15551230000" />
        </Field>
        <Field label={t('int.otp.field.to.locale')} htmlFor="wiz-test-locale">
          <Select id="wiz-test-locale" value={locale} onChange={(e) => setLocale(e.target.value)}>
            <option value="">{t('wiz.test.localeDefault')}</option>
            {slot.locales.map((l) => (
              <option key={l} value={l}>
                {l}
              </option>
            ))}
          </Select>
        </Field>
      </FormGrid>

      <Stack gap={2} role="status" aria-live="polite">
        {record !== undefined && outcome !== undefined && (
          <Card as="div">
            <Stack gap={3}>
              <Notice tone={outcome.tone}>{t(outcome.messageId, { to: record.maskedTo })}</Notice>
              {record.providerMessageId !== undefined && <Text as="p" role="caption" tone="tertiary" mono>{t('wiz.test.messageId', { id: record.providerMessageId })}</Text>}
              {(record.status === 'REJECTED' || record.status === 'FAILED') && (
                <Text as="p" role="caption" tone="tertiary">
                  {failureWords(record.failureReason)}
                  {record.failureCode !== undefined && <Text mono> · {t('wiz.test.code', { code: record.failureCode })}</Text>}
                </Text>
              )}
              {polling && <Text as="p" role="caption" tone="tertiary">{t('wiz.test.polling')}</Text>}
              {pollStopped && !outcome.terminal && <Text as="p" role="caption" tone="tertiary">{t('wiz.test.pollStopped')}</Text>}
            </Stack>
          </Card>
        )}
      </Stack>

      <Card as="div">
        <Stack as="ul" gap={2}>
          {(['whatsapp', 'sms'] as const).map((c) => {
            const s = status[c];
            const verdict = testVerdict(s);
            const last = s.lastTest;
            return (
              <Cluster gap={3} justify="between" wrap as="li" key={c}>
                <Text role="caption" tone="tertiary">{last !== undefined ? t('wiz.test.last', { channel: v('verification.channel', c), when: when(last.sentAt) }) : t('wiz.test.none', { channel: v('verification.channel', c) })}</Text>
                <Cluster gap={3} wrap>
                  {verdict === 'stale' && last?.slotVersion !== undefined && s.version !== undefined && <Text role="caption" tone="tertiary">{t('wiz.test.stale', { tested: last.slotVersion, current: s.version })}</Text>}
                  <Badge tone={verdict === 'passed' ? 'success' : verdict === 'untested' ? 'neutral' : 'warning'}>{t(`wiz.verdict.${verdict}`)}</Badge>
                </Cluster>
              </Cluster>
            );
          })}
        </Stack>
      </Card>

      <StepFooter onBack={onBack}>
        <Button onClick={onNext}>{t('wiz.continue')}</Button>
        <Button variant="primary" disabled={busy || !canSend} onClick={() => void send()}>
          {busy ? t('int.test.busy') : t('int.test.send')}
        </Button>
      </StepFooter>
    </>
  );
}

// ---- 7. review + enable ------------------------------------------------------------------------------------------

type Pending = { readonly kind: 'enable' } | { readonly kind: 'disable'; readonly channel: OtpChannel };

function EnableStep({ status, refresh, onBack }: StepProps): ReactElement {
  const { runtime } = useAdmin();
  const { t } = useT();
  const { v } = useVocab();
  const readiness = (['whatsapp', 'sms'] as const).map((c) => enableReadiness(status, c));
  const [selected, setSelected] = useState<readonly OtpChannel[]>(() => readiness.filter((r) => r.canEnable && !r.requiresAcknowledgement).map((r) => r.channel));
  const [ack, setAck] = useState(false);
  const [pending, setPending] = useState<Pending | undefined>(undefined);
  const [results, setResults] = useState<readonly { readonly tone: Tone; readonly text: string }[]>([]);
  const [busy, setBusy] = useState(false);
  const plan = channelsToEnable(status, selected);
  const needsAck = enableNeedsAcknowledgement(plan);
  const label = (c: OtpChannel): string => v('verification.channel', c);
  const allLive = status.whatsapp.live && status.sms.live;

  const spec: ConfirmSpec | undefined =
    pending === undefined
      ? undefined
      : pending.kind === 'enable'
        ? { title: t('wiz.enable.confirm.title'), body: t('wiz.enable.confirm.body', { channels: plan.map((p) => label(p.channel)).join(' + ') }), confirmLabel: t('wiz.enable.confirm.ok'), cancelLabel: t('wiz.cancel'), tone: 'danger' }
        : { title: t('wiz.disable.confirm.title', { channel: label(pending.channel) }), body: t('wiz.disable.confirm.body', { channel: label(pending.channel) }), confirmLabel: t('wiz.disable.confirm.ok'), cancelLabel: t('wiz.cancel'), tone: 'danger' };

  const resolve = async (r: ConfirmResult): Promise<void> => {
    const p = pending;
    setPending(undefined);
    if (!r.confirmed || p === undefined) return;
    setBusy(true);
    const out: { tone: Tone; text: string }[] = [];
    if (p.kind === 'enable') {
      for (const item of plan) {
        const res = await setOtpChannelEnabled(runtime, item.channel, { enabled: true, ...(item.expectedVersion !== undefined ? { expectedVersion: item.expectedVersion } : {}) });
        out.push(res.ok ? { tone: 'success', text: t('wiz.enable.done', { channel: label(item.channel) }) } : { tone: 'danger', text: t('wiz.enable.failed', { channel: label(item.channel), code: res.error.code }) });
      }
      setSelected([]);
      setAck(false);
    } else {
      const version = status[p.channel].version;
      const res = await setOtpChannelEnabled(runtime, p.channel, { enabled: false, ...(version !== undefined ? { expectedVersion: version } : {}) });
      out.push(res.ok ? { tone: 'success', text: t('wiz.disable.done', { channel: label(p.channel) }) } : { tone: 'danger', text: t('wiz.enable.failed', { channel: label(p.channel), code: res.error.code }) });
    }
    setResults(out);
    await refresh();
    setBusy(false);
  };

  return (
    <>
        <Notice tone="danger">{t('wiz.enable.warning')}</Notice>
      {allLive && (
          <Notice tone="success">{t('wiz.enable.allLive')}</Notice>
      )}
      <Stack as="ul" gap={3}>
        {readiness.map((r) => {
          const slot = status[r.channel];
          const verdict = testVerdict(slot);
          return (
            <Card as="li" key={r.channel}>
              <Stack gap={3}>
                <Cluster gap={3} justify="between" wrap>
                  <Text as="h3" role="label">{label(r.channel)}</Text>
                  <Cluster gap={3} wrap>
                    <Badge tone={r.live ? 'success' : 'neutral'}>{r.live ? t('wiz.enable.live') : t('wiz.enable.notLive')}</Badge>
                    <Badge tone={verdict === 'passed' ? 'success' : verdict === 'untested' ? 'neutral' : 'warning'}>{t(`wiz.verdict.${verdict}`)}</Badge>
                    {slot.version !== undefined && <Text role="caption" tone="tertiary" mono>{t('wiz.enable.version', { v: slot.version })}</Text>}
                  </Cluster>
                </Cluster>
                {(r.blockers.length > 0 || r.warnings.length > 0) && (
                  <Stack as="ul" gap={2}>
                    {r.blockers.map((b) => (
                      <li key={b}>
                        <Notice tone="danger">{t(`wiz.block.${b}`)}</Notice>
                      </li>
                    ))}
                    {r.warnings.map((w) => (
                      <li key={w}>
                        <Notice tone="warning">{t(`wiz.warn.${w}`)}</Notice>
                      </li>
                    ))}
                  </Stack>
                )}
                <Cluster gap={3} wrap>
                  {r.enabled ? (
                    <Button disabled={busy} onClick={() => setPending({ kind: 'disable', channel: r.channel })}>
                      {t('wiz.enable.disable')}
                    </Button>
                  ) : (
                    r.canEnable && (
                      <CheckRow id={`wiz-enable-${r.channel}`} checked={selected.includes(r.channel)} onChange={(checked) => setSelected((cur) => (checked ? [...cur.filter((c) => c !== r.channel), r.channel] : cur.filter((c) => c !== r.channel)))}>
                        {t('wiz.enable.select', { channel: label(r.channel) })}
                      </CheckRow>
                    )
                  )}
                </Cluster>
              </Stack>
            </Card>
          );
        })}
      </Stack>

      {needsAck && (
        <CheckRow id="wiz-enable-ack" checked={ack} onChange={setAck}>
          {t('wiz.enable.ack')}
        </CheckRow>
      )}
      <Stack gap={2} role="status" aria-live="polite">
        {results.map((r, i) => (
          <Notice key={i} tone={r.tone}>
            {r.text}
          </Notice>
        ))}
      </Stack>
      {plan.length === 0 && !allLive && <Text as="p" role="caption" tone="tertiary">{t('wiz.enable.nothingSelected')}</Text>}

      <StepFooter onBack={onBack}>
        <Button variant="primary" disabled={busy || plan.length === 0 || (needsAck && !ack)} onClick={() => setPending({ kind: 'enable' })}>
          {t('wiz.enable.submit')}
        </Button>
      </StepFooter>
      <ConfirmDialog spec={spec} open={pending !== undefined} onResolve={(r) => void resolve(r)} />
    </>
  );
}
